import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useState} from "react";
import {useClubSubscription} from "../../hooks/useClubSubscription";
import {useJwtUser} from "../../JWTAuthProvider";
import {pricingFacts} from "../../pricing/plans";
import {PLUS_TRIAL, plusTrialOn} from "../../pricing/paymentsV3";
import services from "../../services";
import PButton from "../PButton";

/**
 * THE Plus trial prompt — the one component that offers the $2.99 trial, and the only places it
 * appears: where a Plus feature is in front of the organiser (a FeatureLock or FeatureGate on the
 * Dashboard, the club players CRM, the club leaderboard, branding, sponsors, the desk extras…),
 * the plan-choosing modals (ClubTierUpgradeModal, PlansModal) and the Billing page. Never on an
 * event page, the top bar, the sidebar or the pass gate (Oct 2026).
 *
 * Renders nothing while plus_trial_enabled is off, with no club, on a plan, or once the trial was
 * used; one line while it runs. The Billing page's PlusTrialCard keeps the fuller card (cancel).
 */
export default function PlusTrialPrompt({clubUid, compact = false}: {clubUid?: string; compact?: boolean}) {
    const user: any = useJwtUser();
    const uid: string | undefined = clubUid || user?.community?.uid || user?.club?.uid || user?.primary_club_uid;
    const {plusTrial: trial} = useClubSubscription(uid);
    const [busy, setBusy] = useState(false);
    if (!uid || !plusTrialOn() || !trial || !trial.enabled || trial.converted) return null;
    const f = pricingFacts();

    if (trial.active) {
        const endsOn = trial.ends_at ? new Date(trial.ends_at).toLocaleDateString(undefined, {day: 'numeric', month: 'short'}) : '';
        return (
            <p className="text-xs text-default-500" data-plus-trial="active">
                {trial.cancelled ? PLUS_TRIAL.cancelledTitle(endsOn) : PLUS_TRIAL.activeTitle(endsOn)}
            </p>
        );
    }
    if (!trial.eligible) return null;

    const start = async () => {
        setBusy(true);
        try {
            const {redirect_url} = await services.plusTrial.start(uid);
            window.location.href = redirect_url;
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || e?.error || 'Could not start the trial.'});
            setBusy(false);
        }
    };

    return (
        <div className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border-2 border-secondary-200 bg-secondary-50/60 dark:bg-secondary-900/10 ${compact ? 'px-3 py-2' : 'px-4 py-3'}`}
             data-plus-trial="offer">
            <div className="flex min-w-0 items-start gap-2">
                <Icon icon="material-symbols-light:workspace-premium-outline" className="mt-0.5 shrink-0 text-xl text-secondary" />
                <div className="min-w-0 text-sm">
                    <div className="font-semibold">{PLUS_TRIAL.title(f)}</div>
                    <div className="text-xs text-default-600">Card required. {PLUS_TRIAL.thenLine(f)}</div>
                </div>
            </div>
            <PButton size={compact ? 'sm' : 'md'} color="secondary" onPress={start} isLoading={busy}>{PLUS_TRIAL.cta(f)}</PButton>
        </div>
    );
}
